<!--
 * @Author: Ygm
 * @Date: 2024-08-02 15:24:56
 * @LastEditors: Ygm
 * @LastEditTime: 2024-08-05 20:51:41
 * @Description: 个人名片组件
 -->
<script>
import { noticeFatherOpenIM } from '@/utils/postMessage'
import { getUserInfoToIdReq } from '@/apis/public'
import { readLocal } from '@/view/public/common'
import { noticeOpenIm } from '@/utils/toAndroid'

export default {
  name: 'BusinessCard',
  props: {
    // 是否显示弹框
    visible: {
      type: Boolean,
      default: false
    },
    // 用户ID
    userId: {
      type: String,
      default: ''
    },
    // 页面类型，用于区分是新开页面打开还是在当前页打开（new：新开页 old：当前页）
    pageType: {
      type: String,
      default: 'old'
    },
    domEvent: {
      type: Object | String,
      default: null
    }
  },
  data() {
    return {
      isVisible: false,
      popoverStyle: {},
      userInfo: {
        uid: '',
        userName: '',
        realName: '',
        mobileNo: '',
        org: {
          uid: '',
          orgCode: '',
          orgDisplayCode: '',
          orgName: '',
          orgShortName: '',
          orgLevel: null
        },
        department: null,
        duties: null
      },
      userMap: {
        duties: '职务',
        mobileNo: '手机',
        orgName: '机构',
        departmentName: '部门'
      }
    }
  },

  computed: {
    // 用户主体信息
    userDetails() {
      const { department, duties, mobileNo, org } = this.userInfo
      const { orgShortName, orgDisplayCode } = org || {}
      const departmentName = department && Array.isArray(department) && department[0].departmentName
      const orgName = orgShortName ? orgShortName || org.orgName : null
      const resultObj = {
        orgName,
        departmentName,
        duties,
        mobileNo
      }
      // 返回有值的对象
      const resultObjKeys = Object.keys(resultObj)
      resultObjKeys.forEach(key => {
        if (!resultObj[key]) {
          delete resultObj[key]
        }
      })

      return resultObj
    }
  },

  watch: {
    // 监听isVisible的变化
    async visible(val) {
      if (val) {
        await this.getUserInfoToId()
        this.getPosition()
        this.addOutsideClickListener()
      } else {
        this.removeOutsideClickListener()
      }
    }
  },

  beforeDestroy() {
    this.removeOutsideClickListener()
  },

  methods: {
    // 通过用户id获取用户信息
    async getUserInfoToId() {
      if (!this.userId) return //无用户信息则终止
      const params = { userId: this.userId }
      const res = await getUserInfoToIdReq(params)
      if (res.result) {
        this.userInfo = res.data || {}
      } else {
        this.$message.warn(res.message || '获取用户信息失败')
      }

      this.isVisible = this.visible // 确保isVisible的值与visible的值保持一致，并且在接口请求后再打开弹框
    },

    // 根据文字视口的位置，动态改变浮窗的定位，并且让窗口显示在可视区域
    getPosition() {
      this.$nextTick(() => {
        const textElement = this.domEvent || this.$refs.cardText
        const popoverContainer = this.$refs.popoverContainer
        if (textElement && popoverContainer) {
          const rect = textElement.getBoundingClientRect()
          const popoverRect = popoverContainer.getBoundingClientRect()
          const popoverWidth = popoverRect.width
          const popoverHeight = popoverRect.height
          const padding = 15 // 防止弹出物碰到边缘

          let top = rect.top - popoverHeight - padding
          let left = rect.left

          // 如果浮窗超出屏幕顶部，将其放置在文字元素下方
          if (top < padding) {
            top = rect.bottom + padding
          }

          // 如果浮窗超出屏幕右侧，调整位置
          if (left + popoverWidth > window.innerWidth - padding) {
            left = window.innerWidth - popoverWidth - padding
          }

          // 如果浮窗超出屏幕左侧，调整位置
          if (left < padding) {
            left = padding
          }

          // 如果浮窗超出屏幕底部，将其放置在文字元素上方
          if (top + popoverHeight > window.innerHeight - padding) {
            top = rect.top - popoverHeight - padding
            // 如果仍然超出顶部，设置最小顶部距离
            if (top < padding) {
              top = padding
            }
          }

          this.popoverStyle = {
            top: `${top}px`,
            left: `${left}px`
          }
        }
      })
    },

    // 通知门户打开IM
    onOpenIM() {
      const { uid, userName, realName } = this.userInfo
      const params = { uid, userName: realName, userId: uid }
      const isApp = readLocal('client') === 'APP'

      isApp && noticeOpenIm(params) // 通知安卓打开IM
      // 非移动端或不是新开页面，通知web端打开IM
      if (!isApp || this.pageType === 'old') {
        this.onChannel(params)
      }

      const parentWindow = window.opener // 获取父级window
      if (parentWindow && this.pageType === 'new') {
        window.open('', '/base/main').focus() // 返回父级窗口
      }

      this.close() // 通知后，关闭浮窗
    },

    // 跨标签通信
    onChannel(params) {
      const channel = new BroadcastChannel('IM_Channel')
      channel.postMessage(params)
    },

    // 关闭弹框
    close() {
      this.isVisible = false
      this.$emit('close', false)
    },

    // 添加全局点击事件监听器
    addOutsideClickListener() {
      document.addEventListener('click', this.handleOutsideClick)
    },

    // 移除全局点击事件监听器
    removeOutsideClickListener() {
      document.removeEventListener('click', this.handleOutsideClick)
    },

    // 处理点击浮窗外部的情况
    handleOutsideClick(event) {
      const popover = this.$refs.popover
      if (popover && !popover.contains(event.target)) {
        this.close()
      }
    }
  }
}
</script>

<template>
  <div class="business-card">
    <div ref="cardText" class="txt-hide">
      <slot name="card-text" />
    </div>

    <!-- 浮窗内容 -->
    <div
      v-if="isVisible && visible"
      :style="popoverStyle"
      ref="popoverContainer"
      class="popover-container"
      @click.stop
    >
      <div ref="popover" class="popover-top">
        <p>
          <a-avatar :size="40" :style="{ backgroundColor: '#d3e3fd' }">
            <svg-icon
              width="1.25em"
              height="1.25em"
              icon-class="bc_user"
              style="vertical-align: -4px"
            />
          </a-avatar>
          <span class="user-name">{{ userInfo.realName }}</span>
        </p>

        <!-- 打开IM对话框 -->
        <span @click.stop="onOpenIM">
          <svg-icon width="18px" height="18px" class="pointer" icon-class="dialogue" />
        </span>
      </div>

      <div class="popover-content">
        <template v-for="(val, key) in userDetails">
          <p class="popover-content__item">
            <span class="item-title">{{ userMap[key] }}：</span>
            {{ val }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.business-card {
  cursor: initial;

  .popover-container {
    position: fixed;
    width: 292px;
    //height: 220px;
    padding: 16px;
    font-size: 14px;
    border-radius: 4px;
    color: #606872;
    background: #f0f5fc;
    box-shadow:
      rgba(0, 0, 0, 0.1) 0 0 4.4px 0,
      rgba(0, 0, 0, 0.2) 0 4.4px 8.8px 0;
    z-index: 9999;

    /* 顶部信息 */
    .popover-top {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .user-name {
        margin: 0 0 0 16px;
        font-size: 16px;
        color: #303233;
      }
    }

    /* 主体内容 */
    .popover-content {
      margin: 16px 0 0;
      text-align: left;
      font-size: 14px;

      .item-title {
        color: #aaaeb2;
      }

      &__item:not(:last-child) {
        margin: 0 0 16px;
        word-break: break-all;
      }
    }
  }

  // 文本框溢出隐藏
  .txt-hide {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BusinessCard/index.vue